import { useId } from "react";

// SQL pipeline: tag pattern → stream → window → group → view → tags.
// Three columns on desktop, two on tablets, one on phones (CSS toggles
// which variant renders, so no layout JS is needed).
const STEPS = [
  { t: "Tag pattern", s: "Pumps/{pump}/*", accent: "var(--mk-cyan)" },
  { t: "CREATE STREAM", s: "typed inputs", accent: "var(--mk-amber)" },
  { t: "OVER 30s", s: "moving window", accent: "var(--mk-amber)" },
  { t: "GROUP BY {pump}", s: "one instance per key", accent: "var(--mk-amber)" },
  { t: "MATERIALIZED VIEW", s: "continuous result", accent: "var(--mk-amber)" },
  { t: "Output tags", s: "back in the tag tree", accent: "var(--mk-green)" },
];

const LABEL = "Pipeline from an Ignition tag pattern through CREATE STREAM, a moving window, a keyed GROUP BY and a materialized view, ending in output tags.";

function StepBox({ w, h, st }: { w: number; h: number; st: (typeof STEPS)[number] }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const gradId = `step-grad-${id}`;
  return (
    <>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#1E2740" />
          <stop offset="1" stopColor="#121829" />
        </linearGradient>
      </defs>
      <rect width={w} height={h} rx="2" className="node" style={{ fill: `url(#${gradId})` }} />
      <rect width="4" height={h} rx="2" fill={st.accent} />
      <text x="20" y={h / 2 - 7} className="svg-l" style={{ fontSize: 15 }}>{st.t}</text>
      <text x="20" y={h / 2 + 17} className="svg-s" style={{ fontSize: 12.5 }}>{st.s}</text>
    </>
  );
}

function Vertical() {
  const w = 300, h = 84, gap = 26;
  const height = STEPS.length * (h + gap) - gap + 20;
  return (
    <svg className="mk-svg mk-pipe-v" viewBox={`0 0 ${w + 20} ${height}`} role="img" aria-label={LABEL}>
      {STEPS.map((st, i) => {
        const y = 10 + i * (h + gap);
        const link = `M${w / 2} ${h}v${gap}`;
        return (
          <g key={st.t} transform={`translate(10 ${y})`}>
            <StepBox w={w} h={h} st={st} />
            {i < STEPS.length - 1 && (
              <>
                <path d={link} className="pipe-lit" style={{ opacity: 0.9 }} />
                <circle r="4" className="particle" style={{ offsetPath: `path('${link}')`, ["--dur" as string]: "1.2s", ["--delay" as string]: `${i * 0.2}s` }} />
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Snake({ cols, className }: { cols: number; className: string }) {
  // Boxes read left → right along each row; a connector drops from the last
  // box of a row down and back to the first box of the next row.
  const w = 210, h = 84, gapX = 34, gapY = 40;
  const rows = Math.ceil(STEPS.length / cols);
  const width = 20 + cols * w + (cols - 1) * gapX + 20;
  const height = 10 + rows * h + (rows - 1) * gapY + 10;
  return (
    <svg className={`mk-svg ${className}`} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={LABEL}>
      {STEPS.map((st, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const x = 20 + col * (w + gapX);
        const y = 10 + row * (h + gapY);
        const last = i === STEPS.length - 1;
        const link = col < cols - 1
          ? `M${w} ${h / 2}h${gapX}`
          : `M${w / 2} ${h} v${gapY / 2} H${-(cols - 1) * (w + gapX) + w / 2} v${gapY / 2}`;
        return (
          <g key={st.t} transform={`translate(${x} ${y})`}>
            <StepBox w={w} h={h} st={st} />
            {!last && (
              <>
                <path d={link} className="pipe-lit" style={{ opacity: 0.9 }} />
                <circle r="4" className="particle" style={{ offsetPath: `path('${link}')`, ["--dur" as string]: col < cols - 1 ? "1.2s" : "2.6s", ["--delay" as string]: `${i * 0.2}s` }} />
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function PipelineGraphic() {
  return (
    <>
      <Snake cols={3} className="mk-pipe-3" />
      <Snake cols={2} className="mk-pipe-z" />
      <Vertical />
    </>
  );
}
